import { useEffect, useRef, useState } from "react";
import { deleteFindingInvestigation, getFindingInvestigation, listFindingInvestigations, saveFindingInvestigation } from "./alertTriageClient.js";
import { runQuery, rowsToObjects } from "./analyticsClient.js";
import { discoveryMembersSpec, withDiscoverySubject, memberCursor } from "./discoveryQueries.js";

export const MAX_COLLECTED_FINDINGS = 5000;
const validId = (id) => typeof id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
const errorCopy = (error) => error?.serverMessage || (error?.name === "CollectionError" ? error.message : "Could not update the investigation. Your saved findings are retained. Retry or reload the investigation.");
const fail = (message) => Object.assign(new Error(message), { name: "CollectionError" });
const isMissing = (error) => error?.status === 404 || error?.grpcCode === "NOT_FOUND";

export async function enumerateCollection({ timeRange, filters = [], relationship = null, expectedCount, subject, contextKey, signal, onProgress = () => {}, query = runQuery }) {
    if (!Number.isSafeInteger(expectedCount) || expectedCount < 1 || expectedCount > MAX_COLLECTED_FINDINGS) throw fail("Select between 1 and 5,000 findings to add.");
    const members = [], seen = new Set();
    let cursor = null;
    for (let page = 0; page <= 100; page++) {
        signal?.throwIfAborted();
        const rows = rowsToObjects(await query(withDiscoverySubject(discoveryMembersSpec({ timeRange, filters, relationship, cursor }), subject), { signal, requestContext: contextKey }));
        for (const row of rows) {
            if (seen.has(row.finding_id) || !row.report_id || !row.trace_id || !row.finding_id) throw fail("The selection changed while collecting findings. Refresh it before adding.");
            seen.add(row.finding_id); members.push(row);
        }
        onProgress(members.length);
        if (members.length > expectedCount) throw fail("The selection changed while collecting findings. Refresh it before adding.");
        if (rows.length < 50) break;
        cursor = memberCursor(rows.at(-1));
    }
    signal?.throwIfAborted();
    if (members.length !== expectedCount) throw fail("The selection changed while collecting findings. Refresh it before adding.");
    return members;
}

export function useFindingInvestigation(contextKey, { restore = true, canWrite = false, startWithInvestigations = false, startWithOverview = false } = {}) {
    const storageKey = `discovery-investigation:${contextKey}`;
    const [id, setId] = useState(() => {
        if (!restore) return null;
        const fromUrl = new URLSearchParams(window.location.search).get("investigation");
        let stored = null;
        try { stored = sessionStorage.getItem(storageKey); } catch { /* URL restoration still works without browser storage. */ }
        return validId(fromUrl) ? fromUrl : validId(stored) ? stored : null;
    });
    const [record, setRecord] = useState(null), [loading, setLoading] = useState(false), [busy, setBusy] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const [expanded, setExpanded] = useState(false), [workspace, setWorkspace] = useState(() => restore && new URLSearchParams(window.location.search).get("investigating") === "1");
    const [library, setLibrary] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return !workspace && (params.get("securityView") === "investigations" || (!params.has("securityView") && startWithInvestigations));
    });
    const [overview, setOverview] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return !workspace && !library && (params.get("securityView") === "overview" || (startWithOverview && !params.has("securityView") && !params.has("discovery")));
    });
    const [lastSavedChange, setLastSavedChange] = useState(null);
    const [membershipVersion, setMembershipVersion] = useState(null);
    const [newCollection, setNewCollection] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [error, setError] = useState(null), [progress, setProgress] = useState("");
    const [list, setList] = useState({ investigations: [], nextPageToken: "" });
    const [listLoading, setListLoading] = useState(false);
    const [listStatus, setListStatus] = useState(() => { const value = new URLSearchParams(window.location.search).get("investigationStatus"); return ["open", "closed"].includes(value) ? value : ""; });
    const [listPages, setListPages] = useState([""]);
    const [pageHistory, setPageHistory] = useState({ member: [""], activity: [""] });
    const live = useRef(true), state = useRef({}), work = useRef(null), controller = useRef(null), lock = useRef(false), creating = useRef(null);
    state.current = { id, record, workspace, library, overview, canWrite, pageHistory, listStatus, createOpen };
    function remember(nextId, investigating = false, push = false, showLibrary = state.current.library, showOverview = state.current.overview) {
        const url = new URL(window.location.href);
        if (!investigating || nextId !== url.searchParams.get("investigation")) url.searchParams.delete("investigationPanel");
        if (nextId) url.searchParams.set("investigation", nextId); else url.searchParams.delete("investigation");
        if (investigating && nextId) url.searchParams.set("investigating", "1"); else url.searchParams.delete("investigating");
        url.searchParams.set("securityView", investigating || showLibrary ? "investigations" : showOverview ? "overview" : "discovery");
        if (url.searchParams.get("mode") === "security") url.searchParams.set("adminView", investigating || showLibrary ? "alerts" : showOverview ? "security-overview" : "discovery");
        if (showLibrary && state.current.listStatus) url.searchParams.set("investigationStatus", state.current.listStatus); else url.searchParams.delete("investigationStatus");
        const historyState = { ...window.history.state, investigationWorkspace: investigating ? (push ? contextKey : window.history.state?.investigationWorkspace ?? null) : null };
        if (push && typeof historyState.idx === "number") historyState.idx++;
        window.history[push ? "pushState" : "replaceState"](historyState, "", url);
        window.dispatchEvent(new PopStateEvent("popstate", { state: historyState }));
        try { if (nextId) sessionStorage.setItem(storageKey, nextId); else sessionStorage.removeItem(storageKey); } catch { /* Persistence remains server-backed and linked from the URL. */ }
    }
    useEffect(() => {
        live.current = true;
        if (!restore) remember(null);
        else if (overview && !new URLSearchParams(window.location.search).has("securityView")) remember(state.current.id, false, false, false, true);
        const pop = () => {
            const params = new URLSearchParams(window.location.search);
            const nextId = validId(params.get("investigation")) ? params.get("investigation") : null;
            try { if (nextId) sessionStorage.setItem(storageKey, nextId); else sessionStorage.removeItem(storageKey); } catch { /* The URL remains the navigation source. */ }
            setListStatus(["open", "closed"].includes(params.get("investigationStatus")) ? params.get("investigationStatus") : "");
            setOverview(params.get("investigating") !== "1" && params.get("securityView") === "overview");
            setId(nextId); setWorkspace(Boolean(nextId) && params.get("investigating") === "1");
            setLibrary(params.get("investigating") !== "1" && (params.get("securityView") === "investigations" || (!params.has("securityView") && params.get("adminView") === "alerts")));
        };
        window.addEventListener("popstate", pop);
        return () => { live.current = false; controller.current?.abort(); window.removeEventListener("popstate", pop); };
    }, []);
    useEffect(() => {
        if (!id) { setRecord(null); setUnavailable(false); return; }
        if (creating.current === id) { creating.current = null; return; }
        const abort = new AbortController();
        setLoading(true); setError(null); setUnavailable(false);
        getFindingInvestigation(id, { signal: abort.signal }).then((data) => {
            if (!abort.signal.aborted) { setRecord(data); setMembershipVersion(data.version); }
        }, (error) => {
            if (abort.signal.aborted) return;
            if (isMissing(error)) setUnavailable(true);
            else setError(errorCopy(error));
        }).finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [id]);
    const listRequest = useRef(0), listController = useRef(null);
    useEffect(() => () => listController.current?.abort(), []);
    async function loadList(pageToken = "", previous = false, status = state.current.listStatus) {
        if (lock.current || work.current) return;
        const request = ++listRequest.current;
        listController.current?.abort();
        const listAbort = new AbortController(); listController.current = listAbort;
        state.current.listStatus = status; setListStatus(status);
        if (state.current.library && !pageToken) remember(state.current.id, false, false, true, false);
        setListLoading(true); setError(null);
        try {
            const data = await listFindingInvestigations({ pageToken, status, signal: listAbort.signal });
            if (live.current && request === listRequest.current) {
                setList(data);
                setListStatus(status);
                setListPages((pages) => previous ? pages.slice(0, -1) : pageToken ? [...pages, pageToken].slice(-100) : [""]);
            }
        }
        catch (error) { if (live.current && request === listRequest.current) setError(errorCopy(error)); }
        finally { if (live.current && request === listRequest.current) setListLoading(false); }
    }
    async function refresh(options = {}) {
        if (!state.current.id) return;
        setLoading(true);
        const target = state.current.id;
        try { const data = await getFindingInvestigation(target, { memberPageToken: state.current.pageHistory.member.at(-1), activityPageToken: state.current.pageHistory.activity.at(-1), ...options }); if (live.current && state.current.id === target) { if (state.current.record?.version !== data.version) setMembershipVersion(data.version); setRecord(data); setError(null); state.current.record = data; } return data; }
        catch (error) { if (live.current && state.current.id === target) setError(errorCopy(error)); throw error; }
        finally { if (live.current && state.current.id === target) setLoading(false); }
    }
    async function page(kind, direction) {
        const previous = state.current.pageHistory;
        const trail = previous[kind];
        const next = direction === "previous" ? trail.slice(0, -1) : [...trail, kind === "member" ? state.current.record.nextMemberPageToken : state.current.record.nextActivityPageToken];
        if (!next.length || (direction !== "previous" && !next.at(-1))) return;
        try { await refresh({ [kind === "member" ? "memberPageToken" : "activityPageToken"]: next.at(-1) }); if (live.current) setPageHistory({ ...previous, [kind]: next.slice(-100) }); } catch { /* The current page remains available for retry. */ }
    }
    function select(nextId, openWorkspace = false) {
        if (lock.current || work.current) return;
        const sameInvestigation = nextId && nextId === state.current.id;
        state.current.id = nextId;
        if (!sameInvestigation) { setMembershipVersion(null); state.current.record = null; setRecord(null); setPageHistory({ member: [""], activity: [""] }); }
        setCreateOpen(false);
        work.current = null; setError(null); setProgress(""); setId(nextId); setWorkspace(openWorkspace); setLibrary(false); setNewCollection(!nextId); setExpanded(true);
        const url = new URL(window.location.href);
        for (const key of ["igroup", "isubject", "ifinding", "ievidence"]) url.searchParams.delete(key);
        window.history.replaceState(window.history.state, "", url);
        remember(nextId, openWorkspace, true, false, nextId ? state.current.overview : false);
    }
    async function runWork() {
        if (lock.current || !state.current.canWrite || !work.current) return;
        lock.current = true; setBusy(true); setError(null);
        ++listRequest.current; listController.current?.abort(); setListLoading(false);
        const task = work.current;
        try {
            if (task.conflict) {
                const latest = await getFindingInvestigation(task.id);
                if (!live.current) return;
                task.version = latest.version; task.operation = null; task.conflict = false; if (state.current.id === task.id) { setMembershipVersion(latest.version); setRecord(latest); state.current.record = latest; }
            }
            while (task.offset < task.chunks.length && live.current && state.current.canWrite) {
                const change = task.chunks[task.offset];
                task.operation ??= { ...change, operationId: crypto.randomUUID(), expectedVersion: task.version };
                const result = await saveFindingInvestigation(task.id, task.operation);
                if (!live.current) return;
                if (task.create) {
                    creating.current = result.incidentId; state.current.id = result.incidentId; setId(result.incidentId);
                    setCreateOpen(false); setNewCollection(false); setExpanded(true);
                    remember(result.incidentId);
                    task.create = false;
                }
                if (state.current.id === task.id) {
                    setRecord(result); state.current.record = result;
                    if (change.addFindingIds?.length || change.removeFindingIds?.length || change.setAsideFindingIds?.length || change.restoreFindingIds?.length || change.undoOperationId) setMembershipVersion(result.version);
                    else { const priorVersion = task.version; setMembershipVersion((version) => version ?? priorVersion); }
                    setLastSavedChange(change);
                    const firstPages = { member: [""], activity: [""] }; setPageHistory(firstPages); state.current.pageHistory = firstPages;
                }
                setList((previous) => {
                    const rows = previous.investigations.filter((row) => row.incidentId !== result.incidentId);
                    if (!state.current.listStatus || state.current.listStatus === result.status) rows.unshift(result);
                    return { ...previous, investigations: rows.slice(0, 25) };
                });
                task.version = result.version; task.offset++; task.operation = null;
                task.processed += (change.addFindingIds?.length ?? 0) + (change.removeFindingIds?.length ?? 0) + (change.setAsideFindingIds?.length ?? 0) + (change.restoreFindingIds?.length ?? 0);
                setProgress(task.total ? `${task.processed} of ${task.total} selected findings processed. ${result.findingCount} saved in this investigation.` : "Investigation saved.");
            }
            if (live.current && task.offset === task.chunks.length) { work.current = null; return true; }
        } catch (error) {
            if (live.current) {
                task.conflict = error.grpcCode === "ABORTED";
                setError(errorCopy(error));
            }
        } finally { lock.current = false; if (live.current) setBusy(false); }
    }
    async function collect({ members, timeRange, filters = [], relationship = null, expectedCount, subject, label = "Discovery selection" }) {
        if (!canWrite || lock.current || work.current || state.current.createOpen) return;
        if (state.current.id && state.current.record?.incidentId !== state.current.id) { setExpanded(true); setError("Wait for the selected investigation to load, or reload it before adding findings."); return; }
        if (state.current.record?.status === "closed") { setExpanded(true); setError("This investigation is closed. Open it to reopen it, or start a new investigation."); return; }
        lock.current = true; setBusy(true); setExpanded(true); setError(null);
        const abort = new AbortController(); controller.current = abort;
        const timeout = setTimeout(() => abort.abort(), 90000);
        try {
            const selection = members ?? await enumerateCollection({ timeRange, filters, relationship, expectedCount, subject, contextKey, signal: abort.signal, onProgress: (n) => { if (live.current) setProgress(`Preparing ${n} of ${expectedCount} findings...`); } });
            abort.signal.throwIfAborted();
            const target = state.current.id || crypto.randomUUID();
            // Every chunk of one addition carries the same batch identity, so an
            // undo retires the whole selection rather than the last 100 findings.
            const source = { label, batch: crypto.randomUUID(), filters, relationship, ...(subject ? { subject } : {}) };
            const chunks = [];
            for (let i = 0; i < selection.length; i += 100) chunks.push({ addFindingIds: selection.slice(i, i + 100).map((m) => m.finding_id), timeRange, source });
            if (!chunks.length || selection.length > MAX_COLLECTED_FINDINGS) throw fail("Select between 1 and 5,000 findings to add.");
            work.current = { id: target, version: state.current.record?.version ?? "0", chunks, offset: 0, processed: 0, total: selection.length, operation: null };
            if (!state.current.id) { creating.current = target; setId(target); state.current.id = target; remember(target); }
        } catch (error) { if (live.current) setError(abort.signal.aborted ? "Collection preparation stopped. No new findings were saved." : errorCopy(error)); }
        finally { clearTimeout(timeout); controller.current = null; lock.current = false; if (live.current) setBusy(false); }
        if (live.current && work.current) await runWork();
    }
    async function save(change) {
        if (!state.current.record || lock.current || work.current || !canWrite) return;
        work.current = { id: state.current.id, version: state.current.record.version, chunks: [change], offset: 0, processed: 0, total: 0, operation: null };
        return runWork();
    }
    // Undo is one server-side set operation, so it is a single chunk however
    // many findings the addition brought in.
    async function undoAddition(operationId) {
        if (!state.current.record || lock.current || work.current || !canWrite || !operationId) return;
        work.current = { id: state.current.id, version: state.current.record.version, chunks: [{ undoOperationId: operationId }], offset: 0, processed: 0, total: 0, operation: null };
        return runWork();
    }
    // Setting aside keeps the finding in the case and takes it out of the working
    // list, so it chunks the same way a removal does.
    async function setAsideMembers(findingIds, reason) {
        const unique = [...new Set((findingIds ?? []).filter(Boolean))];
        if (!state.current.record || lock.current || work.current || !canWrite || !unique.length || !reason) return;
        const chunks = [];
        for (let i = 0; i < unique.length; i += 100) chunks.push({ setAsideFindingIds: unique.slice(i, i + 100), setAsideReason: reason });
        work.current = { id: state.current.id, version: state.current.record.version, chunks, offset: 0, processed: 0, total: unique.length, operation: null };
        return runWork();
    }
    async function restoreMembers(findingIds) {
        const unique = [...new Set((findingIds ?? []).filter(Boolean))];
        if (!state.current.record || lock.current || work.current || !canWrite || !unique.length) return;
        const chunks = [];
        for (let i = 0; i < unique.length; i += 100) chunks.push({ restoreFindingIds: unique.slice(i, i + 100) });
        work.current = { id: state.current.id, version: state.current.record.version, chunks, offset: 0, processed: 0, total: unique.length, operation: null };
        return runWork();
    }
    // Removing a hand-picked set still respects the 100-identity limit per
    // operation, so it runs as consecutive chunks under one progress report.
    async function removeMembers(findingIds) {
        const unique = [...new Set((findingIds ?? []).filter(Boolean))];
        if (!state.current.record || lock.current || work.current || !canWrite || !unique.length) return;
        const chunks = [];
        for (let i = 0; i < unique.length; i += 100) chunks.push({ removeFindingIds: unique.slice(i, i + 100) });
        work.current = { id: state.current.id, version: state.current.record.version, chunks, offset: 0, processed: 0, total: unique.length, operation: null };
        return runWork();
    }
    function startNew() {
        if (!state.current.canWrite || lock.current || work.current) return;
        setCreateOpen(true); setExpanded(true); setError(null);
    }
    async function createInvestigation(change) {
        if (!state.current.canWrite || lock.current || work.current || !change.title?.trim()) return;
        work.current = { id: crypto.randomUUID(), version: "0", chunks: [{ ...change, title: change.title.trim() }], offset: 0, processed: 0, total: 0, operation: null, create: true };
        return runWork();
    }
    async function saveMetadata(item, change) {
        if (!state.current.canWrite || lock.current || work.current) return;
        work.current = { id: item.incidentId, version: item.version, chunks: [change], offset: 0, processed: 0, total: 0, operation: null };
        return runWork();
    }
    async function deleteInvestigation(item) {
        if (!item?.canDelete || lock.current || work.current) return;
        lock.current = true; setBusy(true); setError(null);
        ++listRequest.current; listController.current?.abort(); setListLoading(false);
        try {
            await deleteFindingInvestigation(item.incidentId, item.version);
            if (!live.current) return;
            setList((previous) => ({ ...previous, investigations: previous.investigations.filter((row) => row.incidentId !== item.incidentId) }));
            if (state.current.id === item.incidentId) {
                state.current.id = null; state.current.record = null;
                setId(null); setRecord(null); setMembershipVersion(null);
                const firstPages = { member: [""], activity: [""] };
                state.current.pageHistory = firstPages; setPageHistory(firstPages);
                setWorkspace(false); setOverview(false); setLibrary(true);
                remember(null, false, false, true, false);
            }
            return true;
        } catch (error) {
            if (live.current) setError(errorCopy(error));
        } finally {
            lock.current = false; if (live.current) setBusy(false);
        }
    }
    function viewWorkspace(value, { push = true } = {}) {
        setOverview(false); setWorkspace(value); setLibrary(false); remember(state.current.id, value, push, false, false);
    }
    function showLibrary(status = "") {
        const filter = ["open", "closed"].includes(status) ? status : "";
        state.current.listStatus = filter; setListStatus(filter);
        setOverview(false); setWorkspace(false); setLibrary(true); remember(state.current.id, false, true, true, false);
    }
    function showOverview() {
        setOverview(true); setWorkspace(false); setLibrary(false); remember(state.current.id, false, true, false, true);
    }
    return { createOpen, startNew, cancelCreate: () => { if (!lock.current && !work.current) setCreateOpen(false); }, createInvestigation, saveMetadata, deleteInvestigation, listStatus, overview, showOverview, library, showLibrary, newCollection, previousList: listPages.length > 1, loadPreviousList: () => loadList(listPages.at(-2), true), id, record, loading, busy, expanded, setExpanded, workspace, viewWorkspace, error, progress, list, listLoading, loadList, select, refresh, collect, save, undoAddition, removeMembers, setAsideMembers, restoreMembers,
        lastSavedChange, membershipVersion, page, previousMembers: pageHistory.member.length > 1, previousActivity: pageHistory.activity.length > 1, canWrite, retry: runWork, hasPending: Boolean(work.current), cancel: () => { controller.current?.abort(); },
        discardPending: () => { if (!lock.current) { work.current = null; setError(null); setProgress("Unsent changes discarded. Saved findings are retained."); } },
        canCancel: Boolean(controller.current), contextKey, unavailable };
}
